import { useInfiniteQuery } from '@tanstack/react-query';
import type {
  ConversationMessageT,
  FetchChatResponseT,
} from '@lotushealth/lotushealth_js_sdk/schemas';

import { fetchPrimaryChatByUserId } from '@/shared/api/lotus';

import { userChatKeys } from './keys';

/**
 * Backend cap (`CONVERSATION_MESSAGE_PAGE_SIZE`). When a page returns this many
 * messages, there are likely older messages still to fetch.
 */
export const PRIMARY_CHAT_PAGE_SIZE = 50;

/**
 * Cursor-paginated query definition for the primary chat. Each page is up to
 * `PRIMARY_CHAT_PAGE_SIZE` messages, ordered oldest-first within the page.
 * Subsequent pages walk backwards in time using the oldest message id from the
 * previous page as `before`.
 *
 * The `'infinite'` suffix on the queryKey keeps this isolated from any future
 * single-page `useQuery` against the same user, while still being matched by
 * prefix-based `invalidateQueries({ queryKey: userChatKeys.primaryByUser(...) })`
 * calls (e.g. in `CareStateQuerySync`).
 */
export function primaryChatInfiniteQueryDefinition(userId: string) {
  return {
    queryKey: [...userChatKeys.primaryByUser(userId), 'infinite'] as const,
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam, signal }: { pageParam?: string; signal?: AbortSignal }) =>
      fetchPrimaryChatByUserId(userId, pageParam ? { before: pageParam } : undefined, signal),
    getNextPageParam: (lastPage: FetchChatResponseT) => {
      // Backend returns at most PRIMARY_CHAT_PAGE_SIZE messages oldest-first
      // within the page. If we got a full page, there are likely more older
      // messages; cursor backwards from the oldest id we have.
      if (lastPage.messages.length < PRIMARY_CHAT_PAGE_SIZE) {
        return undefined;
      }
      return lastPage.messages[0]?.id;
    },
    meta: {
      toast: {
        title: 'Could not load chat',
        description: 'Primary conversation could not be fetched.',
      },
    },
  } as const;
}

/**
 * Fetch the full primary chat for a user via React Query infinite pagination.
 */
export function usePrimaryChatInfiniteQuery(userId: string | undefined) {
  return useInfiniteQuery({
    ...primaryChatInfiniteQueryDefinition(userId ?? ''),
    enabled: !!userId,
  });
}

export interface FlattenedPrimaryChat {
  /**
   * All loaded messages, ordered oldest-first.
   */
  messages: ConversationMessageT[];
  /**
   * Conversation id for the loaded pages (taken from page 0). `null` if no
   * pages have loaded yet.
   */
  conversationId: string | null;
}

/**
 * Flatten the page array returned by `usePrimaryChatInfiniteQuery` into a
 * single oldest-first list of messages plus a stable `conversationId`.
 *
 * Pages come back in fetch order (newest page first, then progressively older
 * pages), and each page is internally oldest-first. To get a single oldest->
 * newest list, we walk pages in reverse and concatenate their messages.
 */
export function flattenPrimaryChatPages(
  pages: FetchChatResponseT[] | undefined
): FlattenedPrimaryChat {
  if (!pages || pages.length === 0) {
    return { messages: [], conversationId: null };
  }

  const messages: ConversationMessageT[] = [];
  for (let i = pages.length - 1; i >= 0; i--) {
    const page = pages[i];
    if (!page) continue;
    for (const message of page.messages) {
      messages.push(message);
    }
  }

  return {
    messages,
    conversationId: pages[0]?.conversationId ?? null,
  };
}
